import {
  ApiAdditionalMetricsList,
  ApiChartAttribute,
  ApiMetric,
  ApiMetricsNameMap,
} from "@incendium/api";
import {
  Box,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { FunnelDatum } from "@nivo/funnel";
import IncreaseDecrease from "../components/IncreaseDecrease";
import { ClickableTypography } from "Components/UI/ClickableTypography";
import StyledFunnel from "Components/UI/StyledFunnel";
import {
  getUniqueMetrics,
  percentageTopChangeByMetric,
  useDimensionExplorerNavigation,
  useFormatMetric,
} from "features/analytics";
import useMetricExplorerNavigation from "features/analytics/hooks/useMetricExplorerNavigation";
import { motion } from "motion/react";
import { TChartData } from "Interfaces";
import { useCallback, useMemo, useState } from "react";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { ArrowRightAlt } from "@mui/icons-material";
import { useIsMobile } from "Hooks/useIsMobile";

const StyledMetricCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ theme, active }) => ({
  padding: theme.spacing(1),
  position: "absolute",
  background: active ? "white" : "transparent",
  zIndex: 1,
  width: "calc(100% - 16px)",
  border: active ? `2px solid ${theme.palette.primary.main}` : undefined,
  borderRadius: 6,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: "background 0.3s",
  willChange: ["height", "background"],
  top: theme.spacing(1),
  textAlign: "left",

  "&:hover": {
    border: `2px solid ${theme.palette.primary.main}`,
    background: "white",
    transition: "all 0.1s",
    color: theme.palette.primary.main,
  },
}));

const StageChangeBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.2,
  border: `1px solid ${theme.palette.primary.vibrant}`,
  color: theme.palette.primary.vibrant,
  backgroundColor: `${theme.palette.primary.vibrant}14`,
}));

interface IAnalyticsFunnelProps {
  chartData: TChartData[];
  comparisonChartData?: TChartData[];
  useLogFn?: boolean;
  additionalMetrics?: ApiAdditionalMetricsList[];
  chartAttributes?: ApiChartAttribute[];
  metricNames?: ApiMetricsNameMap[];
  showStageChangeFromPrevious?: boolean;
  alwaysShowAdditionalMetrics?: boolean;
}

interface TData extends FunnelDatum {
  comparison?: number;
  realValue: number;
  stageChangeFromPrevious?: number;
}

type FunnelMode = "metricsRow" | "rowsByStage";

function AnalyticsFunnel({
  chartData,
  comparisonChartData,
  useLogFn,
  additionalMetrics,
  chartAttributes,
  metricNames,
  showStageChangeFromPrevious,
  alwaysShowAdditionalMetrics,
}: IAnalyticsFunnelProps) {
  const [activeMetric, setActiveMetric] = useState<string | null>(null);
  const formatMetric = useFormatMetric();
  const onMetricClick = useMetricExplorerNavigation();
  const dimensionClick = useDimensionExplorerNavigation();
  const metricToName = useMetricName();
  const isMobile = useIsMobile();

  const isApiMetric = useCallback((v: string): v is ApiMetric => {
    return (Object.values(ApiMetric) as string[]).includes(v);
  }, []);

  const resolvedFunnelMode: FunnelMode = useMemo(() => {
    // Prefer explicit mapping intent from additional metrics config.
    if ((additionalMetrics || []).some((m) => !!m.dimensionKey)) {
      return "rowsByStage";
    }
    if ((additionalMetrics || []).some((m) => m.key !== undefined)) {
      return "metricsRow";
    }

    // If we have multiple rows with names, treat as "rows per stage"
    // (e.g. [{ name: "Prospect", METRIC_FUNNEL_STAGE_COUNT: 123, ... }, ...]).
    if ((chartData?.length || 0) > 1) return "rowsByStage";
    return "metricsRow";
  }, [additionalMetrics, chartData]);

  const stageMetric: ApiMetric | null = useMemo(() => {
    if (resolvedFunnelMode !== "rowsByStage") return null;
    const row0 = chartData?.[0];
    if (!row0) return null;

    // Prefer funnel stage count if available.
    if (row0[ApiMetric.METRIC_FUNNEL_STAGE_COUNT] !== undefined) {
      return ApiMetric.METRIC_FUNNEL_STAGE_COUNT;
    }

    // Otherwise: pick the first ApiMetric-like key that has a numeric value.
    const candidate = Object.keys(row0)
      .filter((k) => k !== "name")
      .find((k) => isApiMetric(k) && typeof row0[k] === "number");
    return candidate && isApiMetric(candidate) ? candidate : null;
  }, [chartData, resolvedFunnelMode, isApiMetric]);

  const metrics = useMemo(() => {
    if (resolvedFunnelMode === "rowsByStage") {
      // Steps are stage names.
      return (chartData || []).map((r) => String(r.name || "")).filter(Boolean);
    }

    // metricsRow mode: steps are metric keys in the single row.
    const arr = getUniqueMetrics(chartData);
    if (!additionalMetrics) return arr;
    const additional = (additionalMetrics || []).map((ad) => ad.metrics).flat();
    return arr.filter((a) => !additional.includes(a as ApiMetric));
  }, [chartData, additionalMetrics, resolvedFunnelMode]);

  const metricName = useCallback(
    (metric: ApiMetric) => {
      const nameOverride = (metricNames || []).find((m) => m.metric === metric);
      return nameOverride && nameOverride.name
        ? nameOverride.name
        : metricToName(metric);
    },
    [metricToName, metricNames],
  );

  const data = useMemo(() => {
    return metrics.map((k, index) => {
      const isStageRow = resolvedFunnelMode === "rowsByStage";
      const row = isStageRow
        ? (chartData || []).find((r) => String(r.name || "") === k)
        : chartData?.[0];
      const rawValue = isStageRow
        ? Number((row as any)?.[stageMetric as any] || 0)
        : Number((row as any)?.[k] || 0);

      const o: TData = {
        id: k,
        label: isStageRow ? k : metricName(k as ApiMetric),
        value: useLogFn ? Math.log10(rawValue) : rawValue,
        realValue: rawValue,
      };
      if (index > 0) {
        const previousKey = metrics[index - 1];
        const previousRow = isStageRow
          ? (chartData || []).find((r) => String(r.name || "") === previousKey)
          : chartData?.[0];
        const previousValue = isStageRow
          ? Number((previousRow as any)?.[stageMetric as any] || 0)
          : Number((previousRow as any)?.[previousKey] || 0);
        if (previousValue > 0) {
          // Funnel performance shown as "what % of the previous stage made it
          // into this stage" (e.g. 100 -> 10 => 10%).
          o.stageChangeFromPrevious = (o.realValue / previousValue) * 100;
        }
      }
      if (!isStageRow) {
        const foundKey = Object.keys(
          comparisonChartData ? comparisonChartData[0] : [],
        ).findIndex((ck) => ck === k);
        if (foundKey) {
          const change = comparisonChartData
            ? percentageTopChangeByMetric(
                chartData,
                comparisonChartData!,
                k as ApiMetric,
              )
            : undefined;
          o.comparison = change;
        }
      }
      return o;
    });
  }, [
    chartData,
    comparisonChartData,
    metrics,
    useLogFn,
    metricName,
    resolvedFunnelMode,
    stageMetric,
  ]);

  const onFunnelPartClick = useCallback(
    (part, show: boolean) => {
      if (show) {
        setActiveMetric(String(part.data.id));
      } else {
        setActiveMetric(null);
      }
    },
    [setActiveMetric],
  );

  const getAdditionalData = useCallback(
    (targetKey: string | null) => {
      if (!additionalMetrics || !targetKey) {
        return [];
      }
      const found = additionalMetrics.find((am) => {
        return am.dimensionKey === targetKey || String(am.key) === targetKey;
      });
      if (!found) {
        return [];
      }

      if (resolvedFunnelMode === "rowsByStage") {
        const row = (chartData || []).find(
          (r) => String(r.name || "") === targetKey,
        );
        if (!row) return [];
        return (found.metrics || []).map((m) => ({
          metric: m,
          value: Number((row as any)?.[m] || 0),
        }));
      }

      // metricsRow mode: additional metrics live on the single row
      const row0 = chartData[0] as any;
      return (found.metrics || []).map((m) => ({
        metric: m,
        value: Number(row0?.[m] || 0),
      }));
    },
    [chartData, additionalMetrics, resolvedFunnelMode],
  );

  const stageChanges = useMemo(() => {
    if (!showStageChangeFromPrevious || data.length < 2) return [];
    return data
      .map((d, index) => ({
        id: `${d.id}-change`,
        index,
        value: d.stageChangeFromPrevious,
      }))
      .filter((x) => x.index > 0 && x.value !== undefined);
  }, [data, showStageChangeFromPrevious]);

  const onDimensionClick = useCallback(
    (d: TData) => {
      const row = chartData?.find((c) => c.name === d.label);
      if (!row) return;

      const dimensionKey = Object.keys(row).find((k) =>
        k.startsWith("Dimension"),
      );
      if (!dimensionKey) return;

      const attributes: ApiChartAttribute[] = [
        ...(chartAttributes || []),
        {
          key: dimensionKey,
          value: d.label,
        },
      ];
      dimensionClick(dimensionKey, attributes);
    },
    [chartData, chartAttributes, dimensionClick],
  );

  if (!chartData?.length || !data.length) {
    return (
      <Stack
        sx={{
          height: "100%",
          maxHeight: 600,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No data
        </Typography>
      </Stack>
    );
  }

  const additionalMetricsPadding =
    alwaysShowAdditionalMetrics && data[0]?.id != null
      ? (getAdditionalData(String(data[0].id))?.length ?? 0) * 3
      : 0;

  return (
    <Stack
      sx={{
        height: "100%",
        position: "relative",
        maxHeight: 600,
        width: isMobile ? `${data.length * 100}%` : "100%",
      }}
      pt={additionalMetricsPadding}
    >
      <Stack
        component={motion.div}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.2,
          scale: {
            type: "spring",
            bounce: 0.5,
            damping: 9,
            restDelta: 0.001,
          },
        }}
        direction={"row"}
        sx={{
          position: "absolute",
          width: "100%",
          top: 0,
        }}
      >
        {data.map((d) => (
          <Box
            key={d.id}
            sx={{
              flex: 1,
              position: "relative",
              cursor: "pointer",
              scrollSnapAlign: "start",
            }}
            p={1}
            onMouseEnter={() => setActiveMetric(String(d.id))}
            onMouseLeave={() => setActiveMetric(null)}
          >
            <StyledMetricCard active={activeMetric === d.id}>
              <ClickableTypography
                variant="body1"
                color={"inherit"}
                fontWeight={activeMetric === d.id ? 600 : "initial"}
                onClick={
                  isApiMetric(String(d.id))
                    ? () => {
                        onMetricClick(d.id as ApiMetric, chartAttributes);
                      }
                    : () => {
                        onDimensionClick(d);
                      }
                }
              >
                {d.label}
              </ClickableTypography>
              <Stack direction={"row"} alignItems="baseline" spacing={1}>
                <Typography fontSize={28} variant="subtitle1">
                  {formatMetric(
                    (isApiMetric(String(d.id))
                      ? (d.id as ApiMetric)
                      : stageMetric) as ApiMetric,
                    d.realValue,
                    { isKM: true },
                  )}
                </Typography>
                {d.comparison && (
                  <IncreaseDecrease
                    direction={d.comparison >= 0 ? "up" : "down"}
                    value={d.comparison}
                    fontSize={16}
                    useThemeColours
                  />
                )}
              </Stack>
              {(alwaysShowAdditionalMetrics || activeMetric === d.id) && (
                <Stack alignItems={"flex-start"}>
                  {getAdditionalData(String(d.id)).map((ad) => (
                    <ClickableTypography
                      key={ad.metric}
                      onClick={() =>
                        onMetricClick(ad.metric as ApiMetric, chartAttributes)
                      }
                    >
                      {metricName(ad.metric as ApiMetric)}:{" "}
                      {formatMetric(ad.metric as ApiMetric, ad.value, {
                        isKM: true,
                      })}
                    </ClickableTypography>
                  ))}
                </Stack>
              )}
            </StyledMetricCard>
          </Box>
        ))}
      </Stack>
      <StyledFunnel
        data={data}
        onHover={additionalMetrics ? onFunnelPartClick : undefined}
      />
      {stageChanges.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "40%",
            height: 0,
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          {stageChanges.map((change) => (
            <Box
              key={change.id}
              sx={{
                position: "absolute",
                left: `${(change.index / data.length) * 100}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <StageChangeBadge>
                <ArrowRightAlt sx={{ fontSize: 16 }} />
                {change.value?.toFixed(0)}%
              </StageChangeBadge>
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
}

export default AnalyticsFunnel;
